/**
 * Geocodificador especializado CDAV Cauces Hidrográficos (G03)
 *
 * Conecta con WFS CDAV_02_hidrografia (sustituto oficial de
 * DERA_g3_hidrografia desde migración IECA 2026-03-18) para cauces:
 * - cav_02_g03_01_Rio:     Ríos principales
 * - cav_02_g03_02_MasaAgua: Masas de agua (sustituye Arroyo en CDAV)
 *
 * Capas ELIMINADAS en CDAV (no disponibles):
 * - g03_02_Arroyo   → sustituida por cav_02_g03_02_MasaAgua
 * - g03_03_Rambla   → eliminada
 * - g03_05_Barranco → eliminada
 *
 * CARACTERÍSTICAS:
 * - Geometría: LineString → calcula centroide para punto geocodificable
 * - CRS nativo: EPSG:25830 (sin transformación necesaria)
 * - Filtrado por cod_mun (código INE 5 dígitos)
 *
 * Endpoint WFS (RESIL-WFS-S1 — actualizado 2026-03-19):
 * https://www.ideandalucia.es/services/CDAV_02_hidrografia/wfs
 *
 * CONTEXTO PTEL:
 * - Tíjola: 12 cauces sin geocodificar identificados en análisis
 * - Típico en PTELs rurales/montañosos (riesgos hidrológicos)
 *
 * @module services/geocoding/specialized
 * @version 2.0.0 — RESIL-WFS-S1: migración DERA_g3 → CDAV_02_hidrografia
 */

import type { WFSFeature, WFSSearchOptions } from './WFSBaseGeocoder';
import { WFSBaseGeocoder } from './WFSBaseGeocoder';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';

/**
 * Tipos de cauces disponibles en DERA G03
 */
export enum DERAWaterwayType {
  /** Ríos principales */
  RIO = 'RIO',
  /** Arroyos y regatos */
  ARROYO = 'ARROYO',
  /** Ramblas (cauces intermitentes mediterráneos) */
  RAMBLA = 'RAMBLA',
  /** Barrancos */
  BARRANCO = 'BARRANCO',
}

/**
 * Mapeo de tipos a nombres de capas WFS en CDAV_02_hidrografia.
 * RESIL-WFS-S1: prefijo cav_02_ obligatorio.
 * ARROYO → MasaAgua (capa equivalente en CDAV).
 * RAMBLA y BARRANCO eliminadas en CDAV — valor vacío para omitirlas.
 */
export const DERA_WATERWAY_LAYERS: Record<DERAWaterwayType, string> = {
  [DERAWaterwayType.RIO]:      'cav_02_g03_01_Rio',
  [DERAWaterwayType.ARROYO]:   'cav_02_g03_02_MasaAgua', // sustituye g03_02_Arroyo
  [DERAWaterwayType.RAMBLA]:   '',  // eliminada en CDAV_02_hidrografia
  [DERAWaterwayType.BARRANCO]: '',  // eliminada en CDAV_02_hidrografia
};

/**
 * Feature de cauce con propiedades específicas
 */
export interface DERAWaterwayFeature extends WFSFeature {
  properties: {
    /** Código INE del municipio */
    codMun: string;
    /** Longitud del cauce en metros */
    longitud?: number;
    /** Cuenca hidrográfica */
    cuenca?: string;
    /** Capa origen */
    sourceLayer: string;
    /** Tipo de geometría original */
    originalGeomType: 'LineString' | 'MultiLineString';
    [key: string]: unknown;
  };
}

/**
 * Opciones de búsqueda específicas para cauces
 */
export interface DERAWaterwaySearchOptions extends WFSSearchOptions {
  /** Código INE del municipio (5 dígitos) */
  codMun?: string;
  /** Tipo de cauce a consultar */
  waterwayType?: DERAWaterwayType;
}

/**
 * Calcula centroide de geometría LineString/MultiLineString
 * 
 * Para LineString: punto medio del segmento central
 * Para MultiLineString: centroide del primer LineString
 * 
 * @param coordinates - Array de coordenadas [x, y][]
 * @returns Coordenadas del centroide [x, y]
 */
function calculateLineCentroid(coordinates: number[][]): [number, number] {
  if (!coordinates || coordinates.length === 0) {
    throw new Error('Coordenadas vacías para calcular centroide');
  }

  // Para línea simple: punto medio
  if (coordinates.length === 1) {
    return [coordinates[0][0], coordinates[0][1]];
  }

  // Para línea con múltiples vértices: punto medio del segmento central
  const midIndex = Math.floor(coordinates.length / 2);
  const p1 = coordinates[midIndex - 1] || coordinates[0];
  const p2 = coordinates[midIndex];
  
  return [
    (p1[0] + p2[0]) / 2,
    (p1[1] + p2[1]) / 2,
  ];
}

/**
 * Geocodificador especializado DERA Cauces Hidrográficos
 * 
 * Maneja elementos lineales (ríos, arroyos, ramblas, barrancos)
 * calculando centroides para obtener puntos geocodificables.
 * 
 * @example
 * ```typescript
 * const geocoder = new DERAWaterwaysGeocoder();
 * 
 * // Buscar cauces por municipio
 * const features = await geocoder.getByMunicipality('04896'); // Tíjola
 * // Retorna: Río Almanzora, Rambla de Oria, etc.
 * 
 * // Geocodificar por nombre
 * const result = await geocoder.geocodeWithAutoLayer({
 *   name: 'Río Almanzora',
 *   codMun: '04896'
 * });
 * ```
 */
export class DERAWaterwaysGeocoder extends WFSBaseGeocoder {
  
  /** Capa actualmente seleccionada */
  private currentLayer: string = DERA_WATERWAY_LAYERS[DERAWaterwayType.RIO];

  /**
   * Configuración específica para WFS DERA G03 Hidrografía
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      // RESIL-WFS-S1: endpoint canónico CDAV_02_hidrografia (sustituye DERA_g3_hidrografia)
      wfsEndpoint: 'https://www.ideandalucia.es/services/CDAV_02_hidrografia/wfs',
      layerName: this.currentLayer,
      fuzzyThreshold: 0.40, // Más permisivo para nombres de ríos
      timeout: 20000, // 20s (cauces pueden ser geometrías grandes)
      outputSRS: 'EPSG:25830',
    };
  }

  /**
   * Parsea feature GeoJSON de cauce (LineString/MultiLineString)
   * 
   * Calcula centroide de la geometría lineal para obtener punto
   */
  protected parseFeature(feature: unknown): DERAWaterwayFeature | null {
    try {
      const f = feature as { 
        properties?: Record<string, unknown>; 
        geometry?: { type: string; coordinates: number[][] | number[][][] };
        id?: string;
      };
      const props = f.properties || {};
      const geom = f.geometry;

      // Validar geometría lineal
      if (!geom || !geom.coordinates) {
        return null;
      }

      let centroid: [number, number];
      let geomType: 'LineString' | 'MultiLineString';

      if (geom.type === 'LineString') {
        centroid = calculateLineCentroid(geom.coordinates as number[][]);
        geomType = 'LineString';
      } else if (geom.type === 'MultiLineString') {
        // Usar primer LineString del MultiLineString
        const firstLine = (geom.coordinates as number[][][])[0];
        centroid = calculateLineCentroid(firstLine);
        geomType = 'MultiLineString';
      } else {
        // No es geometría lineal
        console.warn('DERAWaterways: Geometría no lineal:', geom.type);
        return null;
      }

      const [x, y] = centroid;

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('DERAWaterways: Coordenadas fuera de rango:', x, y);
        return null;
      }

      // Obtener nombre
      const name = String(props['nombre'] || props['NOMBRE'] || '').trim();
      if (!name) {
        return null;
      }

      return {
        name,
        x,
        y,
        municipality: '',
        province: '',
        address: '',
        properties: {
          codMun: String(props['cod_mun'] || props['COD_MUN'] || ''),
          longitud: props['longitud'] ? Number(props['longitud']) : undefined,
          cuenca: props['cuenca'] ? String(props['cuenca']) : undefined,
          sourceLayer: this.currentLayer,
          originalGeomType: geomType,
        },
      };
    } catch (error) {
      console.error('DERAWaterways: Error parseando feature:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL para cauces
   */
  protected buildCQLFilter(options: DERAWaterwaySearchOptions): string {
    const filters: string[] = [];

    // Filtro principal: código municipal
    if (options.codMun) {
      filters.push(`cod_mun='${this.escapeCQL(options.codMun)}'`);
    }

    // Filtro por nombre
    if (options.name) {
      filters.push(`nombre ILIKE '%${this.escapeCQL(options.name)}%'`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Construye parámetros WFS
   */
  protected buildWFSParams(options: DERAWaterwaySearchOptions): Record<string, string> {
    const params: Record<string, string> = {
      service: 'WFS',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: this.config.layerName,
      outputFormat: 'application/json',
      srsName: this.config.outputSRS,
      count: String(options.maxResults || 100),  // WFS 2.0 canónico, migrado en sub-WMVC 14-may
    };

    const cqlFilter = this.buildCQLFilter(options);
    if (cqlFilter) {
      params['CQL_FILTER'] = cqlFilter;
    }

    return params;
  }

  /**
   * Selecciona capa según tipo de cauce
   */
  private selectLayer(waterwayType: DERAWaterwayType): void {
    this.currentLayer = DERA_WATERWAY_LAYERS[waterwayType];
    this.config.layerName = this.currentLayer;
  }

  /**
   * Detecta tipo de cauce por nombre
   */
  detectWaterwayType(name: string): DERAWaterwayType {
    const lower = name.toLowerCase();
    
    if (lower.includes('rambla')) {
      return DERAWaterwayType.RAMBLA;
    }
    if (lower.includes('barranco')) {
      return DERAWaterwayType.BARRANCO;
    }
    if (lower.includes('arroyo') || lower.includes('regato')) {
      return DERAWaterwayType.ARROYO;
    }
    // Default: río
    return DERAWaterwayType.RIO;
  }

  /**
   * Obtiene TODOS los cauces de un municipio por código INE
   * 
   * Consulta las 4 capas y devuelve todas las features.
   * 
   * @param codMun - Código INE del municipio (5 dígitos)
   * @returns Array de features de todas las capas
   */
  public async getByMunicipality(codMun: string): Promise<DERAWaterwayFeature[]> {
    const allFeatures: DERAWaterwayFeature[] = [];
    const layers = Object.values(DERA_WATERWAY_LAYERS);

    for (const layer of layers) {
      // RESIL-WFS-S1: RAMBLA y BARRANCO eliminadas en CDAV — layer vacío indica no disponible
      if (!layer) continue;
      try {
        this.currentLayer = layer;
        this.config.layerName = layer;

        const params = this.buildWFSParams({ 
          name: '', 
          codMun,
          maxResults: 200,
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data) as DERAWaterwayFeature[];

        // Marcar capa origen
        features.forEach(f => {
          f.properties.sourceLayer = layer;
        });

        allFeatures.push(...features);
      } catch (error) {
        console.warn(`DERAWaterways: Error en capa ${layer} para ${codMun}:`, error);
      }
    }

    // Restaurar capa por defecto
    this.currentLayer = DERA_WATERWAY_LAYERS[DERAWaterwayType.RIO];
    this.config.layerName = this.currentLayer;

    return allFeatures;
  }

  /**
   * Geocodifica un cauce con detección automática de tipo
   * 
   * @param options - Opciones de búsqueda
   * @returns Resultado de geocodificación o null
   */
  public async geocodeWithAutoLayer(options: DERAWaterwaySearchOptions): Promise<GeocodingResult | null> {
    // Detectar tipo y seleccionar capa
    const detectedType = options.waterwayType || this.detectWaterwayType(options.name);
    this.selectLayer(detectedType);

    // Intentar con capa detectada
    let result = await this.geocodeLegacy(options);
    if (result && result.confidence >= 65) {
      result.sourceLayer = `DERA:${this.currentLayer}`;
      return result;
    }

    // Probar otras capas si no encuentra
    const allTypes = Object.values(DERAWaterwayType);
    for (const type of allTypes) {
      if (type === detectedType) continue;

      this.selectLayer(type);
      result = await this.geocodeLegacy(options);

      if (result && result.confidence >= 65) {
        result.sourceLayer = `DERA:${this.currentLayer}`;
        return result;
      }
    }

    // Restaurar capa por defecto
    this.selectLayer(DERAWaterwayType.RIO);
    return result;
  }

  /**
   * Estadísticas del geocodificador
   */
  public override getStats() {
    return {
      ...super.getStats(),
      currentLayer: this.currentLayer,
      availableLayers: Object.values(DERA_WATERWAY_LAYERS),
      supportedTypes: Object.values(DERAWaterwayType),
    };
  }
}

export default DERAWaterwaysGeocoder;
